import React, { useState, useEffect } from 'react';

const EditRoleModal = ({ isOpen, onClose, onEditRole, role, availablePages }) => {
  const [formData, setFormData] = useState({
    roleName: '',
    displayName: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role) {
      setFormData({
        roleName: role.roleName || role.role_name || '',
        displayName: role.displayName || role.display_name || '',
        permissions: role.permissions || []
      });
    }
  }, [role]);

  const handlePermissionChange = (permissionId) => {
    const updatedPermissions = formData.permissions.includes(permissionId)
      ? formData.permissions.filter(p => p !== permissionId)
      : [...formData.permissions, permissionId];

    setFormData({
      ...formData,
      permissions: updatedPermissions
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onEditRole(role.id || role.roleName, formData);
      onClose();
    } catch (error) {
      console.error('Error editing role:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      roleName: '',
      displayName: '',
      permissions: []
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="w-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  ✏️ Редактировать роль
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Основная информация */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Системное имя роли *
                      </label>
                      <input
                        type="text"
                        placeholder="franchise"
                        value={formData.roleName}
                        onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        pattern="[a-zA-Z0-9_\-]+"
                        required
                        disabled={role && role.id} // Если это кастомная роль из таблицы, то ID не менять
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Отображаемое название *
                      </label>
                      <input
                        type="text"
                        placeholder="Франчайзи"
                        value={formData.displayName}
                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Разрешения */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Разрешения</h4>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                      {Object.entries(availablePages).map(([category, pages]) => (
                        <div key={category} className="mb-6 last:mb-0">
                          <h5 className="font-medium text-gray-800 mb-3 pb-1 border-b border-gray-200">
                            {category}
                          </h5>
                          <div className="space-y-2">
                            {pages.map(page => (
                              <label key={page.id} className="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(page.id)}
                                  onChange={() => handlePermissionChange(page.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                                />
                                <span className="text-xl mr-2">{page.icon}</span>
                                <span className="text-sm font-medium text-gray-900">{page.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-700">
                        <strong>Выбранные разрешения ({formData.permissions.length}):</strong>
                        {formData.permissions.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {formData.permissions.map(perm => {
                              const page = Object.values(availablePages).flat().find(p => p.id === perm);
                              return (
                                <span key={perm} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                  {page?.icon} {page?.name || perm}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-blue-600 ml-1">Нет выбранных разрешений</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Кнопки действий */}
                <div className="mt-8 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    Отменить
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !formData.roleName.trim() || !formData.displayName.trim() || formData.permissions.length === 0}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Сохранение...' : 'Сохранить изменения'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRoleModal;